import React, { useMemo, useCallback, useEffect, useState } from "react";
import { useBeveragesV2 } from "../../hooks/useBeveragesV2";
import { categoryImageMap } from "./constants/categories-image-map";
import styles from "./BeverageLandingPages.module.css";
import { Link, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { MorningIcon } from "../../components/Icon/Icons/MorningIcon";
import { AfternoonIcon } from "../../components/Icon/Icons/AfternoonIcon";
import { EveningIcon } from "../../components/Icon/Icons/EveningIcon";
import { AnytimeIcon } from "../../components/Icon/Icons/AnytimeIcon";
import DOMPurify from "dompurify";
import { sendPageViewWhenConsentReady } from "../../utils/adobeDataLayerUtils";
import { MAX_BEVERAGE_DESCRIPTION_LENGTH } from "../../utils/beverageDescriptionLimit";
import { CharacterCount } from "~/components";

export const BeverageV2Confirm = () => {
  const navigate = useNavigate();
  const {
    hasToSpecifyTimeFor,
    setTime,
    splitToTimeAndDrink,
    currentBeverages,
    getBeveragesAi,
  } = useBeveragesV2();
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState("");
  const [wasSent, setWasSent] = useState(false);

  const onDescriptionChange = (e) => {
    setDescription(e.target.value);
  };

  const sendDataToAnalyze = async () => {
    setLoading(true);

    try {
      await getBeveragesAi(description, true);
      setWasSent(true);
    } catch (err) {
      console.log("Error while sending data to analyze", err);
    } finally {
      setLoading(false);
    }
  };

  const itemsToSpecify = useMemo(() => {
    return hasToSpecifyTimeFor(currentBeverages);
  }, [currentBeverages, hasToSpecifyTimeFor]);

  const isMultipleFlow = useMemo(() => {
    return (
      itemsToSpecify.selectedAlcoholicLovKeys.length +
        itemsToSpecify.selectedNonAlcoholicLovKeys.length >
      1
    );
  }, [itemsToSpecify]);

  // Check if a description has time specified (with " at " in description)
  const hasTimeSpecified = (desc) => {
    if (!desc) return false;
    return desc.includes(" at ");
  };

  // Get list of beverages missing time specification
  const beveragesMissingTime = useMemo(() => {
    const missing = [];

    itemsToSpecify.selectedAlcoholicLovKeys.forEach((item) => {
      const desc = currentBeverages.beverageAlcoholicDesc[item];
      if (!hasTimeSpecified(desc)) {
        // Extract drink name (before " at " or full string)
        const drinkName = desc ? desc.split(" at ")[0] : item;
        missing.push(drinkName);
      }
    });

    itemsToSpecify.selectedNonAlcoholicLovKeys.forEach((item) => {
      const desc = currentBeverages.beverageNonAlcoholicDesc[item];
      if (!hasTimeSpecified(desc)) {
        const drinkName = desc ? desc.split(" at ")[0] : item;
        missing.push(drinkName);
      }
    });

    return missing;
  }, [currentBeverages, itemsToSpecify]);

  const saveIfAllSpecified = () => {
    const totalSelected =
      itemsToSpecify.selectedAlcoholicLovKeys.length +
      itemsToSpecify.selectedNonAlcoholicLovKeys.length;

    // If no beverages are missing times, auto-save
    if (beveragesMissingTime.length === 0 && totalSelected > 0) {
      save();
    }
  };

  useEffect(() => {
    saveIfAllSpecified();
    return sendPageViewWhenConsentReady("Beverage V2 Confirm", 'Passions-FavBevs', 'beverages2');
  }, []);

  useEffect(() => {
    if (!isMultipleFlow) {
      return;
    }

    saveIfAllSpecified();
  }, [isMultipleFlow, itemsToSpecify, beveragesMissingTime]);

  useEffect(() => {
    if (
      !itemsToSpecify.selectedAlcoholicLovKeys.length &&
      !itemsToSpecify.selectedNonAlcoholicLovKeys.length
    ) {
      navigate("/beverage2");
    }
  }, [itemsToSpecify]);

  const imgSrc = useMemo(() => {
    const key =
      itemsToSpecify.selectedAlcoholicLovKeys[0] ??
      itemsToSpecify.selectedNonAlcoholicLovKeys[0];
    return categoryImageMap[key] ?? categoryImageMap.tea;
  }, [itemsToSpecify]);

  const selectTimeOfTheDay = useCallback(
    (time, currentItem) => {
      setTime(currentItem, time);
    },
    [setTime],
  );

  const save = () => {
    navigate("/beverage2/save");
  };

  const renderNonAlcoholicItems = useCallback(() => {
    return itemsToSpecify.selectedNonAlcoholicLovKeys.map((item) => {
      const { time } = splitToTimeAndDrink(
        currentBeverages.beverageNonAlcoholicDesc[item],
      );
      return renderItem(item, time);
    });
  }, [itemsToSpecify, currentBeverages]);

  const renderAlcoholicItems = useCallback(() => {
    return itemsToSpecify.selectedAlcoholicLovKeys.map((item) => {
      const { time } = splitToTimeAndDrink(
        currentBeverages.beverageAlcoholicDesc[item],
      );
      return renderItem(item, time);
    });
  }, [itemsToSpecify, currentBeverages]);

  const renderItem = (item, selectedTime) => {
    const timeArr = (selectedTime ?? "").split(", ");
    return (
      <>
        <div className={styles.buttons}>
          {[
            { name: "morning", Icon: MorningIcon },
            { name: "afternoon", Icon: AfternoonIcon },
            { name: "evening", Icon: EveningIcon },
            { name: "anytime", Icon: AnytimeIcon },
          ].map(({ name, Icon }) => {
            return (
              <button
                key={name}
                onClick={() => selectTimeOfTheDay(name, item)}
                className={classNames(
                  styles.timeOfDayButton,
                  "custom_click_track",
                  timeArr.includes(name) ? styles.active : null,
                )}
                data-custom_click_track_value={`BeverageV2|Time Of Day Selection: ${name} for ${item}|internal`}
              >
                <Icon alt={name} className={styles.timeOfDayIcon} />
                <span className={styles.timeOfDayLabel}>{name}</span>
              </button>
            );
          })}
        </div>
      </>
    );
  };

  const renderResponseText = useCallback(() => {
    return (
      currentBeverages.plainTextResponse.replace(
        /\[(.*?)\]/g,
        `<br/><span class="${styles.tag}">$1</span>`,
      ) + `<br/> When do you like to drink it?`
    );
  }, [currentBeverages]);

  return (
    <>
      <main className={styles.main}>
        <figure className={styles.figure}>
          <img className={styles.image} src={imgSrc} />
          <figcaption
            className={classNames(
              styles.caption,
              styles.captionOnTop,
              styles.confirmCaption,
            )}
          >
            {isMultipleFlow ? (
              <>
                <h2 className={classNames(styles.title, styles.confirmTitle)}>
                  Simply delicious!
                </h2>
                <p
                  className={styles.text}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(renderResponseText(), {
                      USE_PROFILES: { html: true },
                    }),
                  }}
                ></p>

                <p className={styles.secondTitle}>
                  {wasSent ? (
                    <>
                      Please specify times for:{" "}
                      <strong>{beveragesMissingTime.join(", ")}</strong>
                    </>
                  ) : (
                    <>
                      <i>When</i> are you sipping them?
                    </>
                  )}
                </p>

                <textarea
                  disabled={loading}
                  onChange={onDescriptionChange}
                  className={styles.textarea}
                  placeholder="Share when you savor them"
                  maxLength={MAX_BEVERAGE_DESCRIPTION_LENGTH}
                ></textarea>
                <CharacterCount
                  value={description}
                  max={MAX_BEVERAGE_DESCRIPTION_LENGTH}
                />
              </>
            ) : (
              <>
                <h2 className={classNames(styles.title, styles.confirmTitle)}>
                  Nice choice!
                </h2>
                <p
                  className={styles.text}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(renderResponseText(), {
                      USE_PROFILES: { html: true },
                    }),
                  }}
                ></p>
                {renderAlcoholicItems()}
                {renderNonAlcoholicItems()}
              </>
            )}

            <div>
              <button
                onClick={isMultipleFlow ? sendDataToAnalyze : save}
                disabled={loading}
                className={`${styles.submit} custom_click_track`}
                data-custom_click_track_value="BeverageV2|Done|internal"
              >
                {loading ? (
                  <>
                    <span className={styles.loader}></span>
                    Analyzing...
                  </>
                ) : (
                  "Save to my Bonvoy Profile"
                )}
              </button>

              <Link
                to="/beverage2"
                className="custom_click_track"
                data-custom_click_track_value="Beverage V2 Confirm|Go Back And Retry|internal"
              >
                Go back and retry
              </Link>
            </div>
          </figcaption>
        </figure>

        <p className={classNames(styles.footer)}>
          Your preferences will be saved to your{" "}
          <a
            href="https://www.marriott.com/sign-in.mi"
            target="_blank"
            rel="noopener noreferrer"
            className="custom_click_track"
            data-custom_click_track_value="Beverage V2 Confirm|Marriott Bonvoy Profile Link|external"
          >
            Marriott Bonvoy profile
          </a>{" "}
          and used by Marriott Group to deliver personalized experiences.
        </p>
      </main>
    </>
  );
};
